import React from "react";
import Link from "theme/components/atoms/Typography/Link";
import CategoriesMenu from "./CategoriesMenu";
import withIsFeatureActive from "web/core/UNSAFE_featureFlags/withIsFeatureActive";
import compose from "recompose/compose";
import { FormattedMessage } from "react-intl";
import BoldOnHover from "theme/components/atoms/Typography/BoldOnHover/BoldOnHover";
import Svg from "theme/components/atoms/Icon/Svg";

const MainCategoryElement = ({
  category,
  selectCategory,
  renderSubNav,
  applicationVersion,
}) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = React.useState(false);

  const tabNav = (e, isSubMenuOpen) => {
    if (e.keyCode === 40) {
      e.preventDefault();
      setIsSubMenuOpen(true);
      moveFocus(e, isSubMenuOpen);
    }

    if (e.keyCode === 38 || e.keyCode === 27) {
      e.preventDefault();
      setIsSubMenuOpen(false);
      moveFocus(e, isSubMenuOpen);
    }

    if (e.keyCode === 32) {
      e.preventDefault();
      setIsSubMenuOpen((prev) => !prev);
      moveFocus(e, isSubMenuOpen);
    }
  };

  const moveFocus = (e, isSubMenuOpen) => {
    const subMenu = document
      .getElementById(`sub-menu-${category.id}`)
      ?.querySelector(".categories-menu__item");
    if (!isSubMenuOpen && subMenu instanceof HTMLElement) {
      subMenu.focus({ preventScroll: true });
    } else if (e.target instanceof HTMLElement) {
      e.target.focus({ preventScroll: true });
    }
  };

  return (
    <>
      {applicationVersion === "1" ? (
        <Link
          to={category.path}
          spanClassName="flex-row-center bold-on-hover--parent"
        >
          <BoldOnHover weight="medium">{category.name}</BoldOnHover>
        </Link>
      ) : (
        <>
          {category.children.length === 0 ? (
            <Link
              to={category.path}
              className="menu-main-categories__link--level-0 bold-on-hover--parent"
              spanClassName="flex-row-center"
              dataText={category.name}
            >
              <BoldOnHover weight="medium">{category.name}</BoldOnHover>
            </Link>
          ) : (
            <div className="menu-main-categories__link-container">
              <Link
                to={category.path}
                appearance="link"
                onMouseEnter={() => {
                  selectCategory();
                  setIsSubMenuOpen(true);
                }}
                onMouseLeave={() => {
                  setIsSubMenuOpen(false);
                }}
                onKeyDown={(e) => tabNav(e, isSubMenuOpen)}
                className="menu-main-categories__link-with-icon menu-main-categories__link--level-0 bold-on-hover--parent"
                spanClassName="flex-row-center"
                dataText={category.name}
                aria-controls={`sub-menu-${category.id}`}
                aria-expanded={isSubMenuOpen}
              >
                <BoldOnHover weight="medium">{category.name}</BoldOnHover>
                <Svg
                  type="keyboard_arrow_down"
                  className="menu-main-categories__icon"
                />
              </Link>
              <CategoriesMenu
                category={category}
                isSubMenuOpen={isSubMenuOpen}
                setIsSubMenuOpen={setIsSubMenuOpen}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

const MenuMainCategories = ({
  data,
  selectMenu,
  renderSubNav,
  enterpriseMenuLink,
  applicationVersion,
}) => {
  if (!enterpriseMenuLink || enterpriseMenuLink === "") {
    enterpriseMenuLink = "#";
  }

  const menuRef = React.useRef(null);
  const [showPrevArrow, setShowPrevArrow] = React.useState(false);
  const [showNextArrow, setShowNextArrow] = React.useState(false);

  // Fonction pour vérifier si les flèches doivent être affichées
  const checkArrows = React.useCallback(() => {
    if (menuRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = menuRef.current;
      setShowPrevArrow(scrollLeft > 0);
      setShowNextArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  // Gestionnaire de défilement
  const handleScroll = (direction) => {
    if (menuRef.current) {
      const scrollAmount = direction === "prev" ? -200 : 200;
      menuRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleWheel = React.useCallback((e) => {
    if (e.deltaY !== 0 && menuRef.current) {
      e.preventDefault();
      menuRef.current.scrollLeft += e.deltaY;
    }
  }, []);

  React.useEffect(() => {
    const menuElement = menuRef.current;
    if (menuElement) {
      menuElement.addEventListener("wheel", handleWheel, { passive: false });
      menuElement.addEventListener("scroll", checkArrows);
      // Vérification initiale
      checkArrows();
      // Vérification lors du redimensionnement
      window.addEventListener("resize", checkArrows);
    }

    return () => {
      if (menuElement) {
        menuElement.removeEventListener("wheel", handleWheel);
        menuElement.removeEventListener("scroll", checkArrows);
        window.removeEventListener("resize", checkArrows);
      }
    };
  }, [handleWheel, checkArrows]);

  return data.navigationMenu && data.navigationMenu.length ? (
    <nav className="menu-main-categories container">
      <div className="menu-main-categories__container">
        <button
          className={`menu-main-categories__arrow menu-main-categories__arrow--prev ${
            !showPrevArrow ? "menu-main-categories__arrow--hidden" : ""
          }`}
          onClick={() => handleScroll("prev")}
          aria-label="Précédent"
        >
          <Svg type="chevron_left" />
        </button>

        <ul className="menu-main-categories__list" ref={menuRef}>
          {enterpriseMenuLink !== "#" && (
            <li className="menu-main-categories__item" key="enterprise">
              <a
                href={enterpriseMenuLink}
                className="menu-main-categories__link--level-0 menu-main-categories__link--enterprise"
              >
                <FormattedMessage
                  id="layout.enterprise"
                  defaultMessage="Company"
                />
              </a>
            </li>
          )}
          {data.navigationMenu.map((category) => (
            <li className="menu-main-categories__item" key={category.id}>
              <MainCategoryElement
                category={category}
                selectCategory={() => selectMenu(`category_${category.id}`)}
                renderSubNav={renderSubNav(`category_${category.id}`)}
                applicationVersion={applicationVersion}
              />
            </li>
          ))}
        </ul>

        <button
          className={`menu-main-categories__arrow menu-main-categories__arrow--next ${
            !showNextArrow ? "menu-main-categories__arrow--hidden" : ""
          }`}
          onClick={() => handleScroll("next")}
          aria-label="Suivant"
        >
          <Svg type="chevron_right" />
        </button>
      </div>
    </nav>
  ) : null;
};

export default compose(withIsFeatureActive("searchFeature", "search"))(
  MenuMainCategories
);
